import type {ReactElement} from 'react';
import type {InputTypes} from '../../../core/entities/reports/constants';
import {convertRadioTypeAnswer} from './convertRadioTypeAnswer';
import {getVisibleCharLengthByStepId} from './getVisibleCharLengthByStepId';

type QuestionAnswer = {
  label?: string;
  value?: string;
  inputType?: InputTypes;
};

/**
 * 選考ステップの質問回答を文字数制限に基づいて表示アイテムを生成する
 * @param questionAnswers 質問回答の配列
 * @param stepId 選考ステップID
 * @returns 表示用のReactコンポーネント配列
 */
export const createLimitedSelectionStepItems = (
  questionAnswers: QuestionAnswer[],
  stepId: number,
): ReactElement[] => {
  let totalCharCount = 0;
  const displayItems: ReactElement[] = [];

  for (let index = 0; index < questionAnswers.length; index++) {
    const item = questionAnswers[index];
    const itemLabel = item?.label;
    const itemValue = convertRadioTypeAnswer(
      item?.value ?? '',
      item?.inputType,
    );

    // ラベルと値の文字数を取得
    const labelLength = itemLabel?.length ?? 0;
    const valueLength = itemValue.length;
    const visibleCharLength = getVisibleCharLengthByStepId(stepId);

    // 以前の合計文字数
    const previousTotalCharCount = totalCharCount;
    // 現在のアイテムのラベルと値を追加した場合の合計文字数
    totalCharCount += labelLength + valueLength;

    // 文字数が規定を超えない場合はそのまま表示
    if (
      previousTotalCharCount + labelLength + valueLength <=
      visibleCharLength
    ) {
      displayItems.push(
        <div key={`selection-step-${index}`} className="mt-16">
          <div className="w-full select-none font-bold text-black-1">
            {itemLabel}
          </div>
          <div className="w-full select-none whitespace-pre-wrap text-black-1 mt-4">
            {itemValue}
          </div>
        </div>,
      );
    } else {
      // ラベルは必ず表示
      const remainingChars =
        visibleCharLength - previousTotalCharCount - labelLength;

      // 残りの文字数があれば値の一部を表示し、「...」で終わる
      if (remainingChars > 0) {
        displayItems.push(
          <div key={`selection-step-${index}`} className="mt-16">
            <div className="w-full select-none font-bold text-black-1">
              {itemLabel}
            </div>
            <div className="w-full select-none whitespace-pre-wrap text-black-1 mt-4">
              {itemValue.substring(0, remainingChars)}...
            </div>
          </div>,
        );
      }

      // 規定の文字数を超えたので、それ以降は表示しない
      break;
    }
  }

  return displayItems;
};
